import { ApolloLink, FetchResult, Observable } from "@apollo/client";

// Background work — warming the routes the user is likely to open next — must not share
// the page-load window with the queries the current page is waiting on: they land in the
// same HTTP batch and compete for the same server capacity. Neither the `load` event nor
// requestIdleCallback can gate it, because both fire within ~300ms while the page's own
// data is still seconds away and the main thread sits idle waiting on the network. What
// background work actually needs to wait for is the client going quiet, so track that.
let inFlightOperations = 0;
let quietTimer: ReturnType<typeof setTimeout> | undefined;
const quietListeners = new Set<() => void>();

// The page-load waterfall is a chain of round trips with gaps between them, so the count
// touches zero several times mid-load. Only a sustained gap means the page is done.
const QUIET_PERIOD_MS = 500;

const scheduleQuietCheck = () => {
  if (quietTimer) {
    clearTimeout(quietTimer);
    quietTimer = undefined;
  }

  if (inFlightOperations > 0 || quietListeners.size === 0) return;

  quietTimer = setTimeout(() => {
    quietTimer = undefined;
    const listeners = [...quietListeners];
    quietListeners.clear();
    listeners.forEach((listener) => listener());
  }, QUIET_PERIOD_MS);
};

/**
 * Calls `listener` once, as soon as no GraphQL operation has been in flight for a short
 * quiet period. Returns an unsubscribe for callers that unmount before it fires.
 */
export const whenGraphqlIsQuiet = (listener: () => void) => {
  quietListeners.add(listener);
  scheduleQuietCheck();

  return () => {
    quietListeners.delete(listener);
  };
};

/** Counts operations through the link chain so `whenGraphqlIsQuiet` knows when to fire. */
export const graphqlActivityLink = new ApolloLink(
  (operation, forward) =>
    new Observable<FetchResult>((observer) => {
      inFlightOperations++;
      scheduleQuietCheck();

      // Completion, failure and an early unsubscribe all end the operation, and an
      // unsubscribe can follow either of the first two — so only the first one counts.
      let settled = false;
      const settle = () => {
        if (settled) return;
        settled = true;
        inFlightOperations = Math.max(0, inFlightOperations - 1);
        scheduleQuietCheck();
      };

      const subscription = forward(operation).subscribe({
        next: (value) => observer.next(value),
        error: (error) => {
          settle();
          observer.error(error);
        },
        complete: () => {
          settle();
          observer.complete();
        },
      });

      return () => {
        settle();
        subscription.unsubscribe();
      };
    }),
);
